import { useCallback } from 'react';

import { useRadarForegroundTracking as useRadarForegroundTrackingBase } from '@sg/radar-fe';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Order-app wrapper for Radar foreground tracking.
 *
 * Reads the feature flag and wires telemetry, then delegates
 * to the platform hook in `@sg/radar-fe`.
 */
export const useRadarForegroundTracking = (params: Params) => {
  const { isCustomerLoggedIn, isReady } = params;

  const isEnabled = useFeatureFlag('CELS-4898-radar-geofence-enabled');
  const isAllUserTrackingEnabled = useFeatureFlag('CELS-5507-order-radar-track-all-users-enabled');
  const { track } = useTelemetry();

  // ─── Derived ─────────────────────────────────────────────────────────

  const shouldTrackUser = isCustomerLoggedIn || isAllUserTrackingEnabled;
  const shouldEnableTracking = isEnabled && isReady && shouldTrackUser;

  // ─── Handlers ────────────────────────────────────────────────────────

  const handleTrackOnce = useCallback(
    (status: string) => {
      track('radar.track_once', { status });
    },
    [track],
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useRadarForegroundTrackingBase({ isEnabled: shouldEnableTracking, onTrackOnce: handleTrackOnce });
};

// ─── Types ──────────────────────────────────────────────────────────────────

type Params = {
  isCustomerLoggedIn: boolean;
  isReady: boolean;
};
